import { useState } from "react";
import { Briefcase, Check, Clock, Lock, TrendingUp } from "lucide-react";
import { Reveal } from "../components/common";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Seo, breadcrumbs } from "../components/Seo";

export default function Brokers() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", location: "", experience: "", current_status: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/brokers", form);
      setDone(true);
      toast.success("Application received. We'll be in touch when opportunities open.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong. Please try again.");
    } finally { setLoading(false); }
  };

  return (
    <div data-testid="brokers-page" className="pt-32">
      <Seo title="Work With Us" path="/brokers" description="Join DNA Finance as a commercial finance broker. Apply to work with a whole-of-market brokerage with access to 165+ UK lenders." jsonLd={breadcrumbs([{ name: "Home", path: "/" }, { name: "Work With Us", path: "/brokers" }])} />
      <section className="container-x mx-auto max-w-[1400px] py-16 grid lg:grid-cols-5 gap-16">
        <Reveal className="lg:col-span-2">
          <div className="inline-flex items-center gap-2 border border-lime/40 px-4 py-2 mb-6">
            <Briefcase className="w-4 h-4 text-lime" />
            <span className="text-xs uppercase tracking-[0.25em] text-lime">Work With Us</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-black tracking-tighter leading-[0.95]">For self-employed commercial finance brokers</h1>
          <p className="text-grey text-lg mt-6 leading-relaxed">We partner with a small number of experienced, self-employed brokers who share our standards and want access to our lender panel, systems and support.</p>

          <div className="mt-8 border border-lime/30 bg-lime/5 p-6" data-testid="capacity-notice">
            <div className="flex items-center gap-3 mb-3"><Clock className="w-5 h-5 text-lime" /><span className="text-sm uppercase tracking-widest text-lime font-semibold">At Capacity</span></div>
            <p className="text-white/90 leading-relaxed">Due to our selective process, we are currently <span className="font-semibold">at capacity for brokers in 2026</span>. We expect to open new opportunities in <span className="text-lime font-semibold">Q2 2027</span>.</p>
            <p className="text-grey text-sm mt-3">Register your interest below and we'll contact you directly when places become available.</p>
          </div>

          <ul className="mt-8 space-y-3">
            {["Access to 165+ UK lenders", "Competitive commission structure", "Back-office and case support", "Marketing and brand support", "A genuinely selective, quality-first panel"].map((f) => (
              <li key={f} className="flex items-center gap-3"><Check className="w-5 h-5 text-lime shrink-0" /><span className="text-white/90">{f}</span></li>
            ))}
          </ul>
        </Reveal>

        <Reveal delay={0.1} className="lg:col-span-3">
          {done ? (
            <div data-testid="brokers-success" className="border border-lime/40 bg-lime/5 p-12 text-center h-full flex flex-col items-center justify-center">
              <div className="w-16 h-16 bg-lime flex items-center justify-center mb-6"><TrendingUp className="w-8 h-8 text-[#1A1A1A]" /></div>
              <h3 className="text-2xl font-bold">Thank you, {form.name.split(" ")[0]}</h3>
              <p className="text-grey mt-3 max-w-sm">Your interest has been registered. As we're at capacity for 2026, we'll reach out directly when opportunities open in Q2 2027.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="border border-[#808080]/30 p-8 md:p-10 space-y-5">
              <h3 className="text-xl font-bold">Register your interest</h3>
              <div className="grid sm:grid-cols-2 gap-5">
                <Field label="Full name" required><input data-testid="broker-name" required value={form.name} onChange={set("name")} className="bf" placeholder="Your full name" /></Field>
                <Field label="Location" ><input data-testid="broker-location" value={form.location} onChange={set("location")} className="bf" placeholder="e.g. Cardiff" /></Field>
                <Field label="Phone" required><input data-testid="broker-phone" required value={form.phone} onChange={set("phone")} className="bf" placeholder="Phone number" /></Field>
                <Field label="Email" required><input data-testid="broker-email" required type="email" value={form.email} onChange={set("email")} className="bf" placeholder="you@email.co.uk" /></Field>
                <Field label="Years' experience"><input data-testid="broker-experience" value={form.experience} onChange={set("experience")} className="bf" placeholder="e.g. 6 years" /></Field>
                <Field label="Current status">
                  <select data-testid="broker-status" value={form.current_status} onChange={set("current_status")} className="bf">
                    <option value="">Select</option>
                    <option>Self-employed broker</option>
                    <option>Employed broker</option>
                    <option>New to broking</option>
                    <option>Other finance background</option>
                  </select>
                </Field>
              </div>
              <Field label="Tell us about yourself"><textarea data-testid="broker-message" value={form.message} onChange={set("message")} rows={4} className="bf resize-none" placeholder="Your experience, specialisms and what you're looking for" /></Field>
              <button data-testid="broker-submit" disabled={loading} className="w-full bg-lime text-[#1A1A1A] font-bold py-4 hover:glow-lime transition-shadow">{loading ? "Submitting..." : "Register Interest"}</button>
              <p className="text-xs text-grey text-center flex items-center justify-center gap-2"><Lock className="w-3 h-3" /> Your details are handled confidentially in line with our privacy policy.</p>
            </form>
          )}
        </Reveal>
      </section>
      <style>{`.bf{width:100%;background:#111;border:1px solid rgba(128,128,128,.3);padding:.75rem 1rem;color:#fff;outline:none}.bf:focus{border-color:#D8FF00}`}</style>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-wide text-grey mb-2">{label}{required && <span className="text-lime"> *</span>}</label>
      {children}
    </div>
  );
}
